import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Upload, Download, Play, Square, RefreshCcw, ExternalLink, FileJson, Image,
  Loader2, CheckCircle2, AlertTriangle, Clock3, FolderOpen, ListOrdered, BookOpen
} from 'lucide-react';

import { useApp } from '../context/AppContext';
import {
  listProjects,
  getProject,
  uploadImage,
  pollJob,
  getAnalysis,
  exportAnalysis,
  getAnalysisChartUrl,
} from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { ScrollArea } from '../components/ui/scroll-area';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const ACTIVE_PROJECT_KEY = 'wavekit.bulkReview.activeProject';
const sessionStorageKey = (projectId) => `wavekit.bulkReview.session.${projectId || 'unassigned'}`;

const readStoredJson = (key) => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const writeStoredJson = (key, value) => {
  if (typeof window === 'undefined') return;
  try {
    if (value == null) {
      window.localStorage.removeItem(key);
      return;
    }
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // ignore storage issues
  }
};

const makeLocalId = () => `bulk_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const createQueueItem = (file) => ({
  id: makeLocalId(),
  fileName: file.name,
  fileSize: file.size,
  fileLastModified: file.lastModified,
  status: 'queued',
  progress: 0,
  error: null,
  jobId: null,
  analysisId: null,
  reviewed: false,
  reviewedAt: null,
  notes: '',
  lessonCorrection: '',
  lessonReviewed: false,
  lessonReviewedAt: null,
  createdAt: new Date().toISOString(),
  runtimeFile: file,
  source: 'upload',
  analysisSummary: null,
});

const sanitizeStoredItem = (item) => {
  const next = { ...item };
  delete next.runtimeFile;

  if (['queued', 'uploading', 'processing'].includes(next.status)) {
    next.status = 'needs_reupload';
    next.progress = 0;
    next.error = next.error || 'Browser session reset before this file finished uploading. Re-add the file to continue.';
  }

  return next;
};

const summarizeAnalysis = (analysis) => {
  if (!analysis) return null;
  const candidates = Array.isArray(analysis.candidates) ? analysis.candidates : [];
  const preferred = candidates.find((candidate) => candidate?.id === analysis.preferred_candidate_id)
    || candidates.find((candidate) => candidate?.preferred)
    || candidates[0]
    || null;

  return {
    analysisId: analysis.id,
    preferredCandidateId: preferred?.id || analysis.preferred_candidate_id || null,
    patternType: preferred?.pattern_type || null,
    confidence: typeof preferred?.confidence === 'number' ? preferred.confidence : null,
    pivotCount: Array.isArray(preferred?.pivots) ? preferred.pivots.length : 0,
    candidateCount: candidates.length,
    processingTimeMs: analysis.processing_time_ms || null,
    createdAt: analysis.created_at || null,
    lastAccessedAt: analysis.last_accessed_at || null,
  };
};

const mergeItemsWithProjectAnalyses = (items, analyses) => {
  const byId = new Map((analyses || []).map((analysis) => [analysis.id, analysis]));
  return (items || []).map((item) => {
    const analysis = item.analysisId ? byId.get(item.analysisId) : null;
    if (!analysis) return item;
    return {
      ...item,
      status: item.status === 'failed' ? 'failed' : 'completed',
      analysisSummary: summarizeAnalysis(analysis),
    };
  });
};

const formatDateTime = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return '—';
  }
};

const formatBytes = (value) => {
  const size = Number(value || 0);
  if (!Number.isFinite(size) || size <= 0) return '—';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(2)} MB`;
};

const toLocalImageUrl = (value) => {
  if (!value || typeof value !== 'string') return null;
  if (/^(data:|blob:|https?:|file:)/i.test(value)) return value;
  const normalized = value.replace(/\\/g, '/');
  if (/^[a-z]:\//i.test(normalized)) {
    return `file:///${encodeURI(normalized)}`;
  }
  return encodeURI(normalized);
};

const formatWeakLabelStrength = (value) => {
  if (!value) return '—';
  if (value === 'validated') return 'Validated';
  if (value === 'working') return 'Working';
  if (value === 'exploratory') return 'Exploratory';
  return value;
};

const getLearningReview = (item) => item?.learningReview || null;

const getLearningSummary = (item) => {
  const learningReview = getLearningReview(item);
  if (learningReview?.summary) return learningReview.summary;
  if (learningReview?.primaryLesson) return learningReview.primaryLesson;
  if (item?.weakLabel?.patternHints?.length) {
    return `Pattern hints: ${item.weakLabel.patternHints.join(', ')}`;
  }
  if (item?.notes) return item.notes.slice(0, 180);
  return 'No derived lesson yet.';
};

const statusTone = {
  queued: 'bg-white/5 text-[#a1a1aa] border-white/10',
  uploading: 'bg-[#f5b400]/10 text-[#f5b400] border-[#f5b400]/20',
  processing: 'bg-[#f5b400]/10 text-[#f5b400] border-[#f5b400]/20',
  completed: 'bg-[#00f5d4]/10 text-[#00f5d4] border-[#00f5d4]/20',
  failed: 'bg-[#ff3b30]/10 text-[#ff3b30] border-[#ff3b30]/20',
  needs_reupload: 'bg-[#ff3b30]/10 text-[#ff3b30] border-[#ff3b30]/20',
  imported: 'bg-[#60a5fa]/10 text-[#60a5fa] border-[#60a5fa]/20',
};

export default function BulkReview() {
  const navigate = useNavigate();
  const { setCurrentProject } = useApp();
  const fileInputRef = useRef(null);
  const importInputRef = useRef(null);
  const stopRequestedRef = useRef(false);
  const mountedRef = useRef(true);

  const [projects, setProjects] = useState([]);
  const [projectData, setProjectData] = useState(null);
  const [selectedProjectId, setSelectedProjectId] = useState(() => {
    if (typeof window === 'undefined') return '';
    return window.localStorage.getItem(ACTIVE_PROJECT_KEY) || '';
  });
  const [sessionName, setSessionName] = useState('Bulk Review Session');
  const [items, setItems] = useState([]);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [sessionLoaded, setSessionLoaded] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [refreshingProject, setRefreshingProject] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [exportingPack, setExportingPack] = useState(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      stopRequestedRef.current = true;
    };
  }, [selectedProjectId]);

  useEffect(() => {
    const loadProjects = async () => {
      try {
        setLoadingProjects(true);
        const data = await listProjects();
        if (!mountedRef.current) return;
        setProjects(data || []);
        if (!selectedProjectId && data?.length) {
          const fallbackId = data[0].id;
          setSelectedProjectId(fallbackId);
        }
      } catch (error) {
        console.error(error);
        toast.error('Failed to load projects');
      } finally {
        if (mountedRef.current) setLoadingProjects(false);
      }
    };

    loadProjects();
  }, [selectedProjectId]);

  const refreshProject = useCallback(async (projectId = selectedProjectId) => {
    if (!projectId) return null;
    try {
      setRefreshingProject(true);
      const project = await getProject(projectId);
      if (!mountedRef.current) return null;
      setProjectData(project);
      setCurrentProject(project);
      setItems((prev) => mergeItemsWithProjectAnalyses(prev, project?.analyses || []));
      return project;
    } catch (error) {
      console.error(error);
      toast.error('Failed to refresh project analyses');
      return null;
    } finally {
      if (mountedRef.current) setRefreshingProject(false);
    }
  }, [selectedProjectId, setCurrentProject]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(ACTIVE_PROJECT_KEY, selectedProjectId || '');
    }

    if (!selectedProjectId) {
      setProjectData(null);
      setItems([]);
      setSelectedItemId(null);
      setSessionLoaded(true);
      return;
    }

    const stored = readStoredJson(sessionStorageKey(selectedProjectId));
    const restoredItems = Array.isArray(stored?.items)
      ? stored.items.map((item) => sanitizeStoredItem(item))
      : [];

    setSessionName(stored?.sessionName || 'Bulk Review Session');
    setItems(restoredItems);
    setSelectedItemId(stored?.selectedItemId || restoredItems[0]?.id || null);
    setSessionLoaded(true);
    refreshProject(selectedProjectId);
  }, [selectedProjectId, refreshProject]);

  useEffect(() => {
    if (!sessionLoaded || !selectedProjectId) return;
    writeStoredJson(sessionStorageKey(selectedProjectId), {
      version: 'wavekit-bulk-review-v1',
      sessionName,
      selectedItemId,
      updatedAt: new Date().toISOString(),
      items: items.map((item) => sanitizeStoredItem(item)),
    });
  }, [items, selectedItemId, sessionLoaded, selectedProjectId, sessionName]);

  const selectedProject = useMemo(
    () => projects.find((project) => project.id === selectedProjectId) || projectData || null,
    [projectData, projects, selectedProjectId],
  );

  useEffect(() => {
    if (!selectedItemId && items[0]?.id) {
      setSelectedItemId(items[0].id);
    }
  }, [items, selectedItemId]);

  const selectedItem = useMemo(
    () => items.find((item) => item.id === selectedItemId) || null,
    [items, selectedItemId],
  );

  const lessonItems = useMemo(
    () => items.filter((item) => item.learningReview || item.weakLabel || item.notes || item.previewImageDataUrl || item.sourceImagePath),
    [items],
  );

  const selectedPreviewSrc = useMemo(() => {
    if (!selectedItem) return null;
    if (selectedItem.analysisId) {
      return getAnalysisChartUrl(selectedItem.analysisId, { view: 'annotated' });
    }
    if (selectedItem.previewImageDataUrl) {
      return selectedItem.previewImageDataUrl;
    }
    return toLocalImageUrl(selectedItem.sourceImagePath);
  }, [selectedItem]);

  const selectedLearningReview = useMemo(
    () => getLearningReview(selectedItem),
    [selectedItem],
  );

  const queueStats = useMemo(() => ({
    total: items.length,
    queued: items.filter((item) => item.status === 'queued').length,
    processing: items.filter((item) => item.status === 'processing' || item.status === 'uploading').length,
    completed: items.filter((item) => item.analysisId).length,
    reviewed: items.filter((item) => item.reviewed).length,
    failed: items.filter((item) => item.status === 'failed' || item.status === 'needs_reupload').length,
  }), [items]);

  const lessonStats = useMemo(() => ({
    total: lessonItems.length,
    reviewed: lessonItems.filter((item) => item.lessonReviewed).length,
    withRules: lessonItems.filter((item) => item.learningReview?.ruleCandidates?.length).length,
    withCommentary: lessonItems.filter((item) => (item.notes || '').trim().length > 0).length,
  }), [lessonItems]);

  const updateItem = useCallback((itemId, updater) => {
    setItems((prev) => prev.map((item) => {
      if (item.id !== itemId) return item;
      return typeof updater === 'function' ? updater(item) : { ...item, ...updater };
    }));
  }, []);

  const handleAddFiles = (fileList) => {
    const files = Array.from(fileList || []);
    if (!selectedProjectId) {
      toast.error('Select a project before adding charts');
      return;
    }
    if (!files.length) return;

    setItems((prev) => {
      const existingKeys = new Set(prev.map((item) => `${item.fileName}:${item.fileSize}:${item.fileLastModified}`));
      const additions = files
        .filter((file) => !existingKeys.has(`${file.name}:${file.size}:${file.lastModified}`))
        .map(createQueueItem);
      if (!additions.length) {
        toast.error('Those files are already in this batch queue');
        return prev;
      }
      return [...additions, ...prev];
    });
  };

  const handleFileInput = (event) => {
    handleAddFiles(event.target.files);
    if (event.target) event.target.value = '';
  };

  const openAnalysisReview = async (item) => {
    if (!item?.analysisId || !selectedProjectId) return;
    navigate(`/analysis/${selectedProjectId}?analysisId=${item.analysisId}`);
  };

  const exportSingleAnalysis = async (item, format) => {
    if (!item?.analysisId) return;
    try {
      const blob = await exportAnalysis(item.analysisId, format);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${item.fileName.replace(/\.[^.]+$/, '') || item.analysisId}.${format}`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
      toast.error(`Failed to export ${format.toUpperCase()} for this chart`);
    }
  };

  const toggleReviewed = (itemId) => {
    updateItem(itemId, (item) => ({
      ...item,
      reviewed: !item.reviewed,
      reviewedAt: !item.reviewed ? new Date().toISOString() : null,
    }));
  };

  const toggleLessonReviewed = (itemId) => {
    updateItem(itemId, (item) => {
      const reviewed = !item.lessonReviewed;
      const learningReview = item.learningReview ? {
        ...item.learningReview,
        reviewStatus: reviewed ? 'reviewed' : 'pending',
      } : item.learningReview;
      return {
        ...item,
        lessonReviewed: reviewed,
        lessonReviewedAt: reviewed ? new Date().toISOString() : null,
        learningReview,
      };
    });
  };

  const updateLessonCorrection = (itemId, value) => {
    updateItem(itemId, (item) => ({
      ...item,
      lessonCorrection: value,
      learningReview: item.learningReview ? {
        ...item.learningReview,
        userCorrection: value,
      } : item.learningReview,
    }));
  };

  const runQueue = useCallback(async () => {
    if (!selectedProjectId) {
      toast.error('Select a project first');
      return;
    }

    const pending = items.filter((item) => item.status === 'queued' && item.runtimeFile);
    if (!pending.length) {
      toast.error('No queued files are ready to analyze');
      return;
    }

    stopRequestedRef.current = false;
    setProcessing(true);

    try {
      for (const item of pending) {
        if (stopRequestedRef.current) break;

        updateItem(item.id, { status: 'uploading', progress: 5, error: null });

        try {
          const upload = await uploadImage(selectedProjectId, item.runtimeFile, {
            uploadSource: 'bulk_review',
            analysisFocus: 'macro',
          });

          updateItem(item.id, { jobId: upload.job_id, status: 'processing', progress: 15 });

          const job = await pollJob(upload.job_id, (jobState) => {
            if (!mountedRef.current) return;
            updateItem(item.id, {
              status: jobState.status === 'completed' ? 'processing' : jobState.status,
              progress: Math.max(15, Number(jobState.progress || 0)),
              error: jobState.error || null,
            });
          }, 300);

          if (!job?.result_id) {
            throw new Error('Analysis job finished without a result id');
          }

          const analysis = await getAnalysis(job.result_id);
          updateItem(item.id, {
            status: 'completed',
            progress: 100,
            analysisId: analysis.id,
            error: null,
            analysisSummary: summarizeAnalysis(analysis),
          });
          setSelectedItemId(item.id);
        } catch (error) {
          console.error(error);
          updateItem(item.id, {
            status: 'failed',
            progress: 0,
            error: error?.message || 'Analysis failed',
          });
        }
      }
    } finally {
      setProcessing(false);
      if (selectedProjectId) {
        refreshProject(selectedProjectId);
      }
      toast.success(stopRequestedRef.current ? 'Bulk queue stopped' : 'Bulk queue finished');
    }
  }, [items, refreshProject, selectedProjectId, updateItem]);

  const handleStop = () => {
    stopRequestedRef.current = true;
  };

  const handleClearSession = () => {
    if (!window.confirm('Clear this bulk review session? Completed analysis records stay in the project.')) return;
    setItems([]);
    setSelectedItemId(null);
    setSessionName('Bulk Review Session');
    if (selectedProjectId) {
      writeStoredJson(sessionStorageKey(selectedProjectId), null);
    }
  };

  const handleExportPack = async () => {
    if (!selectedProjectId || !items.length) {
      toast.error('Nothing to export yet');
      return;
    }

    try {
      setExportingPack(true);
      const enrichedItems = await Promise.all(items.map(async (item) => {
        if (!item.analysisId) {
          return {
            ...sanitizeStoredItem(item),
            analysis: null,
          };
        }
        try {
          const analysis = await getAnalysis(item.analysisId);
          return {
            ...sanitizeStoredItem(item),
            analysis,
          };
        } catch {
          return {
            ...sanitizeStoredItem(item),
            analysis: null,
          };
        }
      }));

      const pack = {
        version: 'wavekit-bulk-review-v1',
        exported_at: new Date().toISOString(),
        session_name: sessionName,
        project_id: selectedProjectId,
        project_name: selectedProject?.name || null,
        item_count: enrichedItems.length,
        items: enrichedItems,
      };

      const blob = new Blob([JSON.stringify(pack, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${(selectedProject?.name || 'bulk-review').replace(/[^a-z0-9_-]+/gi, '_')}.bulk-review.json`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
      toast.error('Failed to export review pack');
    } finally {
      setExportingPack(false);
    }
  };

  const handleImportPack = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const raw = await file.text();
      const pack = JSON.parse(raw);
      const importedItems = Array.isArray(pack?.items)
        ? pack.items.map((item) => ({
          ...sanitizeStoredItem(item),
          id: item.id || makeLocalId(),
          source: 'imported',
          analysisSummary: item.analysisSummary || summarizeAnalysis(item.analysis),
          runtimeFile: null,
          lessonCorrection: item.lessonCorrection || item.learningReview?.userCorrection || '',
          lessonReviewed: Boolean(item.lessonReviewed || item.learningReview?.reviewStatus === 'reviewed'),
          lessonReviewedAt: item.lessonReviewedAt || null,
        }))
        : [];

      const nextProjectId = pack.project_id || selectedProjectId;
      if (nextProjectId) {
        setSelectedProjectId(nextProjectId);
      }
      setSessionName(pack.session_name || file.name.replace(/\.json$/i, '') || 'Imported Bulk Review');
      setItems(importedItems);
      setSelectedItemId(importedItems[0]?.id || null);
      toast.success(`Imported ${importedItems.length} review items`);
    } catch (error) {
      console.error(error);
      toast.error('Invalid bulk review pack');
    } finally {
      if (event.target) event.target.value = '';
    }
  };

  const firstUnreviewed = useMemo(
    () => items.find((item) => item.analysisId && !item.reviewed) || null,
    [items],
  );

  const firstUnreviewedLesson = useMemo(
    () => lessonItems.find((item) => !item.lessonReviewed) || null,
    [lessonItems],
  );

  return (
    <div className="animate-fade-in space-y-6" data-testid="bulk-review-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-white">Bulk Review</h1>
          <p className="text-sm text-[#a1a1aa] mt-1">
            Upload chart batches, analyze them sequentially, review pivots chart-by-chart, and export corrected training packs.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="outline"
            onClick={() => importInputRef.current?.click()}
            className="border-white/10 bg-[#14161a] hover:bg-white/5"
          >
            <FolderOpen className="w-4 h-4 mr-2" /> Import Pack
          </Button>
          <Button
            variant="outline"
            onClick={handleExportPack}
            disabled={exportingPack || !items.length}
            className="border-white/10 bg-[#14161a] hover:bg-white/5"
          >
            {exportingPack ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            Export Pack
          </Button>
          <input ref={importInputRef} type="file" accept="application/json" className="hidden" onChange={handleImportPack} />
        </div>
      </div>

      <Card className="bg-[#14161a] border-white/5">
        <CardContent className="pt-6 grid grid-cols-1 xl:grid-cols-[1.2fr_1fr_auto] gap-4 items-end">
          <div className="space-y-2">
            <label className="text-[11px] uppercase tracking-wider font-bold text-[#a1a1aa]">Project</label>
            <Select value={selectedProjectId} onValueChange={setSelectedProjectId}>
              <SelectTrigger className="bg-[#0e0f12] border-white/10 rounded-sm text-sm">
                <SelectValue placeholder={loadingProjects ? 'Loading projects...' : 'Select project'} />
              </SelectTrigger>
              <SelectContent className="bg-[#14161a] border-white/10">
                {(projects || []).map((project) => (
                  <SelectItem key={project.id} value={project.id}>{project.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <label className="text-[11px] uppercase tracking-wider font-bold text-[#a1a1aa]">Session Name</label>
            <Input
              value={sessionName}
              onChange={(event) => setSessionName(event.target.value)}
              placeholder="Bulk Review Session"
              className="bg-[#0e0f12] border-white/10 focus:border-[#f5b400] rounded-sm"
            />
          </div>

          <div className="flex items-center gap-2 flex-wrap xl:justify-end">
            <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/webp" multiple className="hidden" onChange={handleFileInput} />
            <Button onClick={() => fileInputRef.current?.click()} disabled={!selectedProjectId} className="bg-[#f5b400] text-black hover:bg-[#d9a000] rounded-sm">
              <Upload className="w-4 h-4 mr-2" /> Add Charts
            </Button>
            <Button onClick={runQueue} disabled={processing || !selectedProjectId || !queueStats.queued} className="bg-[#00f5d4]/15 text-[#00f5d4] border border-[#00f5d4]/30 hover:bg-[#00f5d4]/20 rounded-sm">
              <Play className="w-4 h-4 mr-2" /> Analyze Queue
            </Button>
            <Button variant="outline" onClick={handleStop} disabled={!processing} className="border-white/10 bg-[#14161a] hover:bg-white/5 rounded-sm">
              <Square className="w-4 h-4 mr-2" /> Stop
            </Button>
            <Button variant="outline" onClick={() => refreshProject()} disabled={!selectedProjectId || refreshingProject} className="border-white/10 bg-[#14161a] hover:bg-white/5 rounded-sm">
              {refreshingProject ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCcw className="w-4 h-4 mr-2" />} Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
        {[
          ['Queued', queueStats.queued],
          ['Running', queueStats.processing],
          ['Completed', queueStats.completed],
          ['Reviewed', queueStats.reviewed],
          ['Needs Fix', queueStats.failed],
          ['Total', queueStats.total],
        ].map(([label, value]) => (
          <Card key={label} className="bg-[#14161a] border-white/5">
            <CardContent className="pt-4">

              <p className="text-[11px] uppercase tracking-wider text-[#6b7280]">{label}</p>
              <p className="text-2xl font-bold text-white mt-1 font-mono">{value}</p>
            </CardContent>
          </Card>
        ))}
      </div>
      <Tabs defaultValue="review" className="space-y-6">
        <TabsList className="bg-[#14161a] border border-white/5 rounded-sm p-1">
          <TabsTrigger value="review" className="rounded-sm text-xs data-[state=active]:bg-[#f5b400]/10 data-[state=active]:text-[#f5b400]">
            <ListOrdered className="w-3 h-3 mr-1.5" /> Queue Review
          </TabsTrigger>
          <TabsTrigger value="lessons" className="rounded-sm text-xs data-[state=active]:bg-[#f5b400]/10 data-[state=active]:text-[#f5b400]">
            <BookOpen className="w-3 h-3 mr-1.5" /> Final Lessons
          </TabsTrigger>
        </TabsList>

        {/* ─── QUEUE REVIEW TAB ─── */}
        <TabsContent value="review" className="mt-0">
          <div className="grid grid-cols-1 2xl:grid-cols-[440px_minmax(0,1fr)] gap-6">
            <Card className="bg-[#14161a] border-white/5 min-h-[680px]">
              <CardHeader className="pb-3 flex flex-row items-center justify-between space-y-0">
                <CardTitle className="text-white text-base flex items-center gap-2">
                  <ListOrdered className="w-4 h-4 text-[#00f5d4]" /> Batch Queue
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={handleClearSession} disabled={!items.length} className="text-[#a1a1aa] hover:text-white">
                  Clear
                </Button>
              </CardHeader>
              <CardContent className="pt-0">
                <ScrollArea className="h-[620px] pr-3">
                  <div className="space-y-3">
                    {!items.length && (
                      <div className="rounded-sm border border-dashed border-white/10 p-6 text-center text-sm text-[#6b7280]">
                        Add a batch of chart screenshots to start building a review queue.
                      </div>
                    )}
                    {items.map((item) => {
                      const active = item.id === selectedItemId;
                      const summary = item.analysisSummary;
                      return (
                        <button
                          type="button"
                          key={item.id}
                          onClick={() => setSelectedItemId(item.id)}
                          className={`w-full text-left rounded-sm border p-3 transition-colors ${active ? 'border-[#f5b400]/35 bg-[#f5b400]/8' : 'border-white/5 bg-white/[0.02] hover:border-white/10'}`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="text-sm font-medium text-white truncate">{item.fileName}</p>
                                <Badge className={`${statusTone[item.status] || statusTone.queued} border text-[10px]`}>{item.status.replace(/_/g, ' ')}</Badge>
                                {item.reviewed && <Badge className="bg-[#00f5d4]/10 text-[#00f5d4] border-[#00f5d4]/20 text-[10px]">Reviewed</Badge>}
                              </div>
                              <div className="mt-1 flex items-center gap-3 text-[11px] text-[#6b7280] flex-wrap">
                                <span>{formatBytes(item.fileSize)}</span>
                                <span>{formatDateTime(item.createdAt)}</span>
                                {summary?.patternType && <span className="text-[#f5b400] uppercase">{summary.patternType}</span>}
                                {typeof summary?.confidence === 'number' && <span className="font-mono text-white">{summary.confidence.toFixed(1)}%</span>}
                              </div>
                              {(item.status === 'processing' || item.status === 'uploading') && (
                                <Progress value={item.progress || 0} className="mt-3 h-1.5 bg-white/5" />
                              )}
                              {item.error && <p className="mt-2 text-[11px] text-[#ff3b30]">{item.error}</p>}
                            </div>
                            {item.status === 'completed' ? <CheckCircle2 className="w-4 h-4 text-[#00f5d4] mt-0.5" /> : item.status === 'failed' || item.status === 'needs_reupload' ? <AlertTriangle className="w-4 h-4 text-[#ff3b30] mt-0.5" /> : <Clock3 className="w-4 h-4 text-[#f5b400] mt-0.5" />}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>

            <div className="space-y-6">
              <Card className="bg-[#14161a] border-white/5 min-h-[680px]">
                <CardHeader className="pb-3 flex flex-row items-center justify-between space-y-0">
                  <CardTitle className="text-white text-base">Review Detail</CardTitle>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => firstUnreviewed && setSelectedItemId(firstUnreviewed.id)}
                      disabled={!firstUnreviewed}
                      className="border-white/10 bg-[#14161a] hover:bg-white/5"
                    >
                      Next Unreviewed
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => selectedItem && openAnalysisReview(selectedItem)}
                      disabled={!selectedItem?.analysisId}
                      className="border-white/10 bg-[#14161a] hover:bg-white/5"
                    >
                      <ExternalLink className="w-4 h-4 mr-2" /> Review Pivots
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="pt-0 space-y-4">
                  {!selectedItem && (
                    <div className="rounded-sm border border-dashed border-white/10 p-8 text-center text-[#6b7280]">
                      Select a chart from the queue to inspect it.
                    </div>
                  )}

                  {selectedItem && (
                    <>
                      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-4">
                        <div className="rounded-sm border border-white/5 bg-[#0e0f12] overflow-hidden min-h-[360px] flex items-center justify-center">
                          {selectedPreviewSrc ? (
                            <img
                              src={selectedPreviewSrc}
                              alt={selectedItem.fileName}
                              className="w-full h-full object-contain"
                            />
                          ) : (
                            <div className="text-center px-6 text-[#6b7280]">
                              <Image className="w-8 h-8 mx-auto mb-3 text-[#6b7280]" />
                              <p className="text-sm">Run the queue or import a review pack with preview images to inspect this chart.</p>
                            </div>
                          )}
                        </div>
                        <div className="space-y-4">
                          <div className="rounded-sm border border-white/5 bg-white/[0.02] p-4 space-y-2">
                            <p className="text-xs uppercase tracking-wider text-[#6b7280]">File</p>
                            <p className="text-white font-medium break-all">{selectedItem.fileName}</p>
                            <div className="grid grid-cols-2 gap-2 text-xs text-[#a1a1aa]">
                              <span>Status</span>
                              <span className="text-right text-white">{selectedItem.status.replace(/_/g, ' ')}</span>
                              <span>Size</span>
                              <span className="text-right text-white">{formatBytes(selectedItem.fileSize)}</span>
                              <span>Analysis ID</span>
                              <span className="text-right text-white font-mono">{selectedItem.analysisId ? selectedItem.analysisId.slice(0, 8) : '—'}</span>
                              <span>Source</span>
                              <span className="text-right text-white">{selectedItem.source || 'upload'}</span>
                              <span>Preferred</span>
                              <span className="text-right text-white">{selectedItem.analysisSummary?.patternType || '—'}</span>
                              <span>Confidence</span>
                              <span className="text-right text-white font-mono">{typeof selectedItem.analysisSummary?.confidence === 'number' ? `${selectedItem.analysisSummary.confidence.toFixed(1)}%` : '—'}</span>
                            </div>
                          </div>

                          {selectedItem.weakLabel && (
                            <div className="rounded-sm border border-white/5 bg-white/[0.02] p-4 space-y-3">
                              <p className="text-xs uppercase tracking-wider text-[#6b7280]">Weak Label Signals</p>
                              <div className="grid grid-cols-2 gap-2 text-xs text-[#a1a1aa]">
                                <span>Strength</span>
                                <span className="text-right text-white">{formatWeakLabelStrength(selectedItem.weakLabel.labelStrength)}</span>
                                <span>Asset Date</span>
                                <span className="text-right text-white">{selectedItem.weakLabel.assetDate || '—'}</span>
                                <span>Sender</span>
                                <span className="text-right text-white">{selectedItem.weakLabel.sender || '—'}</span>
                                <span>Commentary</span>
                                <span className="text-right text-white">{selectedItem.weakLabel.commentaryCount || 0} notes</span>
                              </div>
                              {Array.isArray(selectedItem.weakLabel.symbols) && selectedItem.weakLabel.symbols.length > 0 && (
                                <p className="text-xs text-[#d4d4d8]">
                                  Symbols: <span className="text-white">{selectedItem.weakLabel.symbols.join(', ')}</span>
                                </p>
                              )}
                              {Array.isArray(selectedItem.weakLabel.patternHints) && selectedItem.weakLabel.patternHints.length > 0 && (
                                <p className="text-xs text-[#d4d4d8]">
                                  Pattern Hints: <span className="text-white">{selectedItem.weakLabel.patternHints.join(', ')}</span>
                                </p>
                              )}
                              {Array.isArray(selectedItem.weakLabel.priceMentions) && selectedItem.weakLabel.priceMentions.length > 0 && (
                                <p className="text-xs text-[#d4d4d8]">
                                  Price Mentions: <span className="text-white">{selectedItem.weakLabel.priceMentions.join(', ')}</span>
                                </p>
                              )}
                              {Array.isArray(selectedItem.weakLabel.confidenceFlags) && selectedItem.weakLabel.confidenceFlags.length > 0 && (
                                <p className="text-xs text-[#6b7280]">
                                  Flags: {selectedItem.weakLabel.confidenceFlags.join(', ')}
                                </p>
                              )}
                            </div>
                          )}

                          <div className="rounded-sm border border-white/5 bg-white/[0.02] p-4 space-y-3">
                            <div className="flex items-center justify-between gap-2">
                              <p className="text-xs uppercase tracking-wider text-[#6b7280]">Review State</p>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => toggleReviewed(selectedItem.id)}
                                disabled={!selectedItem.analysisId}
                                className="border-white/10 bg-[#14161a] hover:bg-white/5"
                              >
                                {selectedItem.reviewed ? 'Unmark Reviewed' : 'Mark Reviewed'}
                              </Button>
                            </div>
                            <Textarea
                              value={selectedItem.notes || ''}
                              onChange={(event) => updateItem(selectedItem.id, { notes: event.target.value })}
                              placeholder="Optional review notes for this chart or pivot correction context"
                              className="min-h-[120px] bg-[#0e0f12] border-white/10 focus:border-[#f5b400]"
                            />
                            <div className="flex items-center gap-2 flex-wrap">
                              <Button variant="outline" size="sm" onClick={() => exportSingleAnalysis(selectedItem, 'json')} disabled={!selectedItem.analysisId} className="border-white/10 bg-[#14161a] hover:bg-white/5">
                                <FileJson className="w-4 h-4 mr-2" /> JSON
                              </Button>
                              <Button variant="outline" size="sm" onClick={() => exportSingleAnalysis(selectedItem, 'png')} disabled={!selectedItem.analysisId} className="border-white/10 bg-[#14161a] hover:bg-white/5">
                                <Image className="w-4 h-4 mr-2" /> PNG
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        {/* ─── FINAL LESSONS TAB ─── */}
        <TabsContent value="lessons" className="mt-0">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            {[
              ['Total Lessons', lessonStats.total],
              ['With Rules', lessonStats.withRules],
              ['With Commentary', lessonStats.withCommentary],
              ['Lesson Reviewed', lessonStats.reviewed],
            ].map(([label, value]) => (
              <Card key={label} className="bg-[#14161a] border-white/5">
                <CardContent className="pt-4">
                  <p className="text-[11px] uppercase tracking-wider text-[#6b7280]">{label}</p>
                  <p className="text-2xl font-bold text-white mt-1 font-mono">{value}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="grid grid-cols-1 2xl:grid-cols-[420px_minmax(0,1fr)] gap-6">
            {/* Lesson Index */}
            <Card className="bg-[#14161a] border-white/5 min-h-[760px]">
              <CardHeader className="pb-3 flex flex-row items-center justify-between space-y-0">
                <CardTitle className="text-white text-base flex items-center gap-2">
                  <BookOpen className="w-4 h-4 text-[#f5b400]" /> Lesson Index
                </CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => firstUnreviewedLesson && setSelectedItemId(firstUnreviewedLesson.id)}
                  disabled={!firstUnreviewedLesson}
                  className="border-white/10 bg-[#14161a] hover:bg-white/5"
                >
                  Next Lesson
                </Button>
              </CardHeader>
              <CardContent className="pt-0">
                <ScrollArea className="h-[700px] pr-3">
                  <div className="space-y-3">
                    {!lessonItems.length && (
                      <div className="rounded-sm border border-dashed border-white/10 p-6 text-center text-sm text-[#6b7280]">
                        Import a review pack to inspect extracted lessons and rule candidates.
                      </div>
                    )}
                    {lessonItems.map((item) => {
                      const active = item.id === selectedItemId;
                      const learningReview = getLearningReview(item);
                      return (
                        <button
                          type="button"
                          key={item.id}
                          onClick={() => setSelectedItemId(item.id)}
                          className={`w-full text-left rounded-sm border p-3 transition-colors ${active ? 'border-[#f5b400]/35 bg-[#f5b400]/8' : 'border-white/5 bg-white/[0.02] hover:border-white/10'}`}
                        >
                          <div className="flex items-center justify-between gap-3">
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="text-sm font-medium text-white truncate">{item.fileName}</p>
                                {item.weakLabel?.labelStrength && (
                                  <Badge className="bg-[#60a5fa]/10 text-[#60a5fa] border-[#60a5fa]/20 text-[10px]">
                                    {formatWeakLabelStrength(item.weakLabel.labelStrength)}
                                  </Badge>
                                )}
                                {item.lessonReviewed && (
                                  <Badge className="bg-[#00f5d4]/10 text-[#00f5d4] border-[#00f5d4]/20 text-[10px]">
                                    Lesson Reviewed
                                  </Badge>
                                )}
                              </div>
                              <p className="mt-2 text-xs text-[#d4d4d8] line-clamp-3">
                                {learningReview?.primaryRule || getLearningSummary(item)}
                              </p>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>

            {/* Lesson Detail */}
            <Card className="bg-[#14161a] border-white/5 min-h-[760px]">
              <CardHeader className="pb-3 flex flex-row items-center justify-between space-y-0">
                <CardTitle className="text-white text-base">Lesson Review Detail</CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => selectedItem && toggleLessonReviewed(selectedItem.id)}
                  disabled={!selectedItem}
                  className="border-white/10 bg-[#14161a] hover:bg-white/5"
                >
                  {selectedItem?.lessonReviewed ? 'Unmark Lesson' : 'Mark Lesson Reviewed'}
                </Button>
              </CardHeader>
              <CardContent className="pt-0 space-y-4">
                {!selectedItem && (
                  <div className="rounded-sm border border-dashed border-white/10 p-8 text-center text-[#6b7280]">
                    Select a lesson item to inspect the derived rules and correct them.
                  </div>
                )}

                {selectedItem && (
                  <>
                    {/* Image + Derived Lesson */}
                    <div className="grid grid-cols-1 xl:grid-cols-[340px_minmax(0,1fr)] gap-4">
                      <div className="rounded-sm border border-white/5 bg-[#0e0f12] overflow-hidden min-h-[320px] flex items-center justify-center">
                        {selectedPreviewSrc ? (
                          <img src={selectedPreviewSrc} alt={selectedItem.fileName} className="w-full h-full object-contain" />
                        ) : (
                          <div className="text-center px-6 text-[#6b7280]">
                            <Image className="w-8 h-8 mx-auto mb-3 text-[#6b7280]" />
                            <p className="text-sm">No preview available for this lesson item.</p>
                          </div>
                        )}
                      </div>

                      <div className="space-y-4">
                        <div className="rounded-sm border border-white/5 bg-white/[0.02] p-4 space-y-3">
                          <p className="text-xs uppercase tracking-wider text-[#6b7280]">Derived Lesson</p>
                          <p className="text-lg font-semibold text-white">{selectedLearningReview?.primaryLesson || 'No primary lesson derived yet.'}</p>
                          <p className="text-sm text-[#d4d4d8]">{selectedLearningReview?.primaryRule || 'No primary rule candidate derived yet.'}</p>
                          {Array.isArray(selectedLearningReview?.confidenceModeTargets) && selectedLearningReview.confidenceModeTargets.length > 0 && (
                            <p className="text-xs text-[#a1a1aa]">
                              Confidence Spotters: <span className="text-white">{selectedLearningReview.confidenceModeTargets.join(', ')}</span>
                            </p>
                          )}
                        </div>

                        <div className="rounded-sm border border-white/5 bg-white/[0.02] p-4 space-y-3">
                          <p className="text-xs uppercase tracking-wider text-[#6b7280]">Commentary</p>
                          <div className="max-h-[220px] overflow-auto whitespace-pre-wrap text-sm text-[#d4d4d8]">
                            {selectedItem.notes || 'No commentary captured for this item yet.'}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Decision Tree + Rule Candidates */}
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                      <div className="rounded-sm border border-white/5 bg-white/[0.02] p-4 space-y-3">
                        <p className="text-xs uppercase tracking-wider text-[#6b7280]">Decision Tree</p>
                        {Array.isArray(selectedLearningReview?.decisionTree) && selectedLearningReview.decisionTree.length > 0 ? (
                          <div className="space-y-2">
                            {selectedLearningReview.decisionTree.map((entry, idx) => (
                              <p key={idx} className="text-sm text-[#d4d4d8]">{entry}</p>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-[#6b7280]">No decision tree extracted yet.</p>
                        )}
                      </div>

                      <div className="rounded-sm border border-white/5 bg-white/[0.02] p-4 space-y-3">
                        <p className="text-xs uppercase tracking-wider text-[#6b7280]">Rule Candidates</p>
                        {Array.isArray(selectedLearningReview?.ruleCandidates) && selectedLearningReview.ruleCandidates.length > 0 ? (
                          <div className="space-y-2">
                            {selectedLearningReview.ruleCandidates.map((entry, idx) => (
                              <p key={idx} className="text-sm text-[#d4d4d8]">{entry}</p>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-[#6b7280]">No rule candidates extracted yet.</p>
                        )}
                        {Array.isArray(selectedLearningReview?.adjacentStructures) && selectedLearningReview.adjacentStructures.length > 0 && (
                          <p className="text-xs text-[#a1a1aa]">
                            Adjacent Structures: <span className="text-white">{selectedLearningReview.adjacentStructures.join(', ')}</span>
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Your Correction */}
                    <div className="rounded-sm border border-white/5 bg-white/[0.02] p-4 space-y-3">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-xs uppercase tracking-wider text-[#6b7280]">Your Correction</p>
                        <span className="text-[11px] text-[#6b7280]">{selectedItem.lessonReviewed ? 'Reviewed' : 'Needs Review'}</span>
                      </div>
                      <Textarea
                        value={selectedItem.lessonCorrection || ''}
                        onChange={(event) => updateLessonCorrection(selectedItem.id, event.target.value)}
                        placeholder="Correct the lesson, rule, or decision tree here. This stays attached to the image so we can compare what the engine learned versus what you intended."
                        className="min-h-[150px] bg-[#0e0f12] border-white/10 focus:border-[#f5b400]"
                      />
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
